import {PERFORMANCE_THRESHOLDS} from '../layout/constants';
import {getAllAxisValues} from '../layout/utils/axisValues';
import {getFitSettingsForMediaType} from '../sizing/getFitSettingsForMediaType';
import {
  getCombinedHistorySpecs,
  getNoRequiredRunNames,
  getPanelMediaKeys,
  shouldAlwaysFilterRunsInQuery,
} from './queryConfigHelpers';
import {
  getPaginatedLoadTier,
  type TrackingConfigContext,
} from './trackingConfigHelper';
import {type MediaPanelModeConfig} from './types';

function getGalleryPageCap({
  mediaType,
  columnCount,
}: Pick<TrackingConfigContext, 'mediaType' | 'columnCount'>): number | null {
  const cols = columnCount > 0 ? columnCount : 1;

  if (mediaType === 'plotly') {
    return PERFORMANCE_THRESHOLDS.MAX_PLOTLY_WITHOUT_PAGINATION;
  }
  if (mediaType === 'image') {
    return (
      Math.floor(PERFORMANCE_THRESHOLDS.MAX_IMAGES_WITHOUT_PAGINATION / cols) *
      cols
    );
  }
  if (mediaType === 'video') {
    return (
      Math.floor(PERFORMANCE_THRESHOLDS.MAX_VIDEOS_WITHOUT_PAGINATION / cols) *
      cols
    );
  }
  return null;
}

export const galleryModeConfig: MediaPanelModeConfig = {
  imageFitAndResolutionConfigFields: [
    'actualSize',
    'fitToDimension',
    'alwaysOriginal',
    'default',
  ],
  filterToRunsWithMediaKey: true,
  // Gallery sizes tiles by width and wraps; it doesn't manage row heights.
  constrainsContentRowHeight: false,
  // Gallery flows when showing media at original size or fit-to-dimension: each
  // tile sizes to its own content and the row wraps. The fit settings are
  // scoped to the panel's media type.
  shouldSizeTilesByContent: (config, mediaType) => {
    const {actualSize, fitToDimension} = getFitSettingsForMediaType(
      config,
      mediaType
    );
    return actualSize || fitToDimension;
  },
  getRequiredRunNames: getNoRequiredRunNames,
  getRunFilterMediaKeys: getPanelMediaKeys,
  shouldFilterRunsInQuery: shouldAlwaysFilterRunsInQuery,
  getHistorySpecs: getCombinedHistorySpecs,
  getColumnAxisValueCount: (gallerySettings, _gridSettings, runs, stepMeta) =>
    getAllAxisValues(gallerySettings.axis, runs, stepMeta).length,
  getPageCap: getGalleryPageCap,
  getLoadTier: ({activeTileCount, layoutTileCount, pageCap}) =>
    getPaginatedLoadTier(activeTileCount, layoutTileCount, pageCap),
  getPaginationTileTotal: ({layoutTileCount}) => layoutTileCount,
};
